import React, { useEffect, useState, useCallback } from "react";
import client from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { toast } from "sonner";
import { ClipboardCheck, Plus, Loader2, Star } from "lucide-react";

const GRADE_COLOR = { EXCEEDS: "#22C55E", MEETS: "#3B82F6", "NEEDS IMPROVEMENT": "#F59E0B", UNSATISFACTORY: "#EF4444" };
const inp = "w-full bg-[#0d0e11] border border-[#2C2D35] px-2.5 py-2 text-xs font-mono text-white outline-none focus:border-[#3B82F6] rounded-sm";

export default function QaReview() {
  const [templates, setTemplates] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tplId, setTplId] = useState("");
  const [cc, setCc] = useState("");
  const [operator, setOperator] = useState("");
  const [scores, setScores] = useState({});
  const [comments, setComments] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, r, s] = await Promise.all([client.get("/qa/templates"), client.get("/qa/reviews"), client.get("/qa/summary")]);
      setTemplates(t.data.templates || []); setReviews(r.data.reviews || []); setSummary(s.data);
      if (!tplId && t.data.templates?.length) setTplId(t.data.templates[0].tpl_id);
    } catch { /* supervisor only */ }
  }, [tplId]);
  useEffect(() => { load(); }, [load]);

  const tpl = templates.find((t) => t.tpl_id === tplId);
  const submit = async () => {
    if (!cc.trim()) { toast.error("Enter the CC# of the call under review"); return; }
    setBusy(true);
    try {
      const { data } = await client.post("/qa/reviews", { cc_number: cc.trim(), template_id: tplId, operator_name: operator, scores, comments });
      toast.success(`Review saved — ${data.total}/${data.max_total} (${data.pct}% · ${data.grade})`, { duration: 8000 });
      setCc(""); setOperator(""); setScores({}); setComments(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Review failed"); }
    setBusy(false);
  };

  return (
    <Layout>
      <PageHeader title="QA Review — Call & Dispatch Performance" sub="Configurable review forms · scoring · operator performance trends" icon={ClipboardCheck} />
      <div className="flex-1 overflow-y-auto p-5" data-testid="qa-review-page">
        {summary && summary.count > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-[#141518] border border-[#2C2D35] px-4 py-3"><div className="font-mono text-[10px] text-[#9CA3AF] uppercase">Reviews Completed</div><div className="font-mono font-bold text-2xl mt-1">{summary.count}</div></div>
            <div className="bg-[#141518] border border-[#2C2D35] px-4 py-3"><div className="font-mono text-[10px] text-[#9CA3AF] uppercase">Average Score</div><div className="font-mono font-bold text-2xl mt-1" style={{ color: summary.avg_pct >= 75 ? "#22C55E" : "#F59E0B" }}>{summary.avg_pct}%</div></div>
            <div className="bg-[#141518] border border-[#2C2D35] px-4 py-3"><div className="font-mono text-[10px] text-[#9CA3AF] uppercase">Exceeds Standard</div><div className="font-mono font-bold text-2xl mt-1 text-[#22C55E]">{summary.grade_counts?.EXCEEDS ?? 0}</div></div>
            <div className="bg-[#141518] border border-[#2C2D35] px-4 py-3"><div className="font-mono text-[10px] text-[#9CA3AF] uppercase">Needs Improvement</div><div className="font-mono font-bold text-2xl mt-1 text-[#F59E0B]">{(summary.grade_counts?.["NEEDS IMPROVEMENT"] ?? 0) + (summary.grade_counts?.UNSATISFACTORY ?? 0)}</div></div>
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* New review */}
          <div className="border border-[#2C2D35] rounded-sm" data-testid="qa-new-review">
            <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold flex items-center gap-2">
              <Plus size={13} className="text-[#22D3EE]" /> NEW PERFORMANCE REVIEW
            </div>
            <div className="p-3 space-y-2.5">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="font-mono text-[9px] text-[#6B7280] uppercase">Call CC# *</label>
                  <input data-testid="qa-cc-input" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="CC-2026-…" className={inp} />
                </div>
                <div>
                  <label className="font-mono text-[9px] text-[#6B7280] uppercase">Operator under review</label>
                  <input data-testid="qa-operator-input" value={operator} onChange={(e) => setOperator(e.target.value)} placeholder="Name (defaults to call creator)" className={inp} />
                </div>
              </div>
              <div>
                <label className="font-mono text-[9px] text-[#6B7280] uppercase">Review form</label>
                <select data-testid="qa-template-select" value={tplId} onChange={(e) => { setTplId(e.target.value); setScores({}); }} className={inp}>
                  {templates.map((t) => <option key={t.tpl_id} value={t.tpl_id}>{t.name}</option>)}
                </select>
              </div>
              {tpl && (
                <div className="space-y-2 border border-[#1E1F24] bg-[#0d0e11] p-2.5 rounded-sm">
                  {tpl.criteria.map((c) => (
                    <div key={c.key} className="flex items-center gap-2" data-testid={`qa-criterion-${c.key}`}>
                      <span className="text-[11px] text-[#E5E7EB] flex-1">{c.label}</span>
                      <div className="flex gap-0.5">
                        {Array.from({ length: c.max + 1 }, (_, v) => (
                          <button key={v} onClick={() => setScores((p) => ({ ...p, [c.key]: v }))}
                            data-testid={`qa-score-${c.key}-${v}`}
                            className={`w-6 h-6 text-[10px] font-mono font-bold rounded-sm border transition-colors ${scores[c.key] === v ? "bg-[#3B82F6] text-white border-[#3B82F6]" : "border-[#2C2D35] text-[#6B7280] hover:text-white"}`}>{v}</button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <textarea data-testid="qa-comments" value={comments} onChange={(e) => setComments(e.target.value)} rows={3}
                placeholder="Reviewer comments, coaching notes, commendations…" className={inp} />
              <button data-testid="qa-submit" onClick={submit} disabled={busy}
                className="flex items-center gap-2 px-4 py-2 text-xs font-head font-bold bg-[#22D3EE] text-black rounded-sm disabled:opacity-50">
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Star size={13} />} SUBMIT REVIEW
              </button>
            </div>
          </div>

          {/* Recent reviews + operator averages */}
          <div className="space-y-4">
            {summary?.by_operator?.length > 0 && (
              <div className="border border-[#2C2D35] rounded-sm">
                <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold">OPERATOR AVERAGES</div>
                {summary.by_operator.map((o) => (
                  <div key={o.operator} className="flex items-center gap-2 px-3 py-2 border-b border-[#1E1F24]">
                    <span className="text-xs text-white flex-1">{o.operator}</span>
                    <span className="font-mono text-[10px] text-[#6B7280]">{o.reviews} review(s)</span>
                    <span className="font-mono text-sm font-bold w-14 text-right" style={{ color: o.avg_pct >= 75 ? "#22C55E" : "#F59E0B" }}>{o.avg_pct}%</span>
                  </div>
                ))}
              </div>
            )}
            <div className="border border-[#2C2D35] rounded-sm" data-testid="qa-review-list">
              <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold">RECENT REVIEWS</div>
              {reviews.length === 0 && <div className="p-3 font-mono text-[11px] text-[#6B7280]">No reviews yet. Score a call using the form.</div>}
              {reviews.slice(0, 20).map((r) => (
                <div key={r.id} className="px-3 py-2 border-b border-[#1E1F24]" data-testid={`qa-review-row-${r.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[11px] font-bold text-[#22D3EE]">{r.cc_number}</span>
                    <span className="text-[11px] text-[#9CA3AF]">{r.template_name}</span>
                    <span className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded-sm border ml-auto" style={{ color: GRADE_COLOR[r.grade], borderColor: `${GRADE_COLOR[r.grade]}55` }}>{r.pct}% · {r.grade}</span>
                  </div>
                  <div className="font-mono text-[9px] text-[#6B7280] mt-0.5">
                    Operator {r.operator_name || "—"} · reviewed by {r.reviewer} · {r.ts ? new Date(r.ts).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : ""}
                  </div>
                  {r.comments && <div className="text-[11px] text-[#E5E7EB] mt-1">{r.comments}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
